import { useState, useEffect, useCallback } from "react";

interface FileNode {
  name: string;
  path: string;
  type: "file" | "dir";
  children?: FileNode[];
}

interface FilesWorkbenchProps {
  projectName: string;
}

export function FilesWorkbench({ projectName }: FilesWorkbenchProps) {
  const [tree, setTree] = useState<FileNode[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(["src"]));
  const [activeFile, setActiveFile] = useState<string | null>(null);
  const [fileContent, setFileContent] = useState("");
  const [fileLang, setFileLang] = useState("text");
  const [highlighted, setHighlighted] = useState("");
  const [loading, setLoading] = useState(false);

  // Fetch file tree
  useEffect(() => {
    fetch("/api/files")
      .then((r) => r.json())
      .then((d) => setTree(d.tree || []))
      .catch(() => {});
  }, []);

  // Fetch file content when active file changes
  useEffect(() => {
    if (!activeFile) return;
    setLoading(true);
    fetch(`/api/files/${activeFile}`)
      .then((r) => r.json())
      .then(async (d) => {
        setFileContent(d.content || "");
        setFileLang(d.language || "text");
        // Try Shiki highlighting
        try {
          const { codeToHtml } = await import("shiki");
          const html = await codeToHtml(d.content || "", {
            lang: mapLang(d.language),
            theme: "github-dark",
          });
          setHighlighted(html);
        } catch {
          setHighlighted("");
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [activeFile]);

  const toggleDir = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  }, []);

  const renderNode = (node: FileNode, depth: number = 0) => {
    const isDir = node.type === "dir";
    const isExpanded = expanded.has(node.path);
    const isActive = activeFile === node.path;

    return (
      <div key={node.path}>
        <div
          className={`list-item ${isActive ? "list-item--active" : ""}`}
          style={{
            paddingLeft: `${depth * 1.2 + 0.4}rem`,
            cursor: "pointer",
            background: isActive ? "var(--shell-accent-bg)" : undefined,
            color: isActive ? "var(--shell-accent)" : undefined,
          }}
          onClick={() => {
            if (isDir) toggleDir(node.path);
            else setActiveFile(node.path);
          }}
        >
          <span style={{ opacity: .4, fontSize: ".7rem", width: "1rem", textAlign: "center" }}>
            {isDir ? (isExpanded ? "▼" : "▶") : ""}
          </span>
          <span style={{ fontWeight: isDir ? 600 : 400 }}>{node.name}</span>
        </div>
        {isDir && isExpanded && node.children?.map((child) => renderNode(child, depth + 1))}
      </div>
    );
  };

  return (
    <div className="slab-grid slab-12" style={{ height: "calc(100vh - 100px)" }}>
      {/* File tree */}
      <div className="block" style={{ overflowY: "auto" }}>
        <h4>Explorer</h4>
        {tree.length === 0 ? (
          <div className="meta">Loading file tree...</div>
        ) : (
          tree.map((node) => renderNode(node))
        )}
      </div>

      {/* Code viewer */}
      <div className="block" style={{ overflowY: "auto" }}>
        {activeFile ? (
          <>
            <h4 style={{ fontFamily: "monospace", fontSize: ".75rem" }}>{activeFile}</h4>
            {loading ? (
              <div className="meta">Loading...</div>
            ) : highlighted ? (
              <div
                className="shiki-container"
                dangerouslySetInnerHTML={{ __html: highlighted }}
                style={{ fontSize: ".75rem", lineHeight: "1.6", overflow: "auto" }}
              />
            ) : (
              <pre style={{
                fontSize: ".72rem",
                lineHeight: "1.6",
                color: "var(--shell-text)",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}>
                {fileContent}
              </pre>
            )}
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", opacity: .3 }}>
            Select a file to view
          </div>
        )}
      </div>
    </div>
  );
}

function mapLang(ext: string): string {
  const map: Record<string, string> = {
    rs: "rust", ts: "typescript", tsx: "tsx", js: "javascript", jsx: "jsx",
    py: "python", go: "go", md: "markdown", json: "json", toml: "toml",
    yaml: "yaml", yml: "yaml", css: "css", html: "html", sh: "bash",
    proto: "protobuf", sql: "sql", dockerfile: "dockerfile",
  };
  return map[ext] || "text";
}
